import api from './api';

// The doctor patient page fires several GETs at once. Without a limit, one
// stalled backend/database call leaves the page on its loading skeleton
// forever. Deliberately per-request rather than a global axios timeout, which
// would also cut off legitimately slow calls (report upload, AI triage).
export const LOAD_TIMEOUT_MS = 15000;
const LOAD = { timeout: LOAD_TIMEOUT_MS };

// ── Patient list & detail ─────────────────────────────────────────────────────
export const doctorPatientAPI = {
  // GET /api/doctor/patients?doctorId=&page=&size=&search=
  list: (params) => api.get('/doctor/patients', { params }),

  // GET /api/doctor/patients/{patientId}?doctorEmail=
  getDetail: (patientId, doctorEmail) =>
    api.get(`/doctor/patients/${patientId}`, { params: { doctorEmail }, ...LOAD }),
};

// ── Doctor's own appointments ────────────────────────────────────────────────
export const doctorAppointmentAPI = {
  // GET /api/doctor/appointments?doctorEmail=&filter=upcoming|past|cancelled|all
  list: (doctorEmail, filter = 'all') =>
    api.get('/doctor/appointments', { params: { doctorEmail, filter }, ...LOAD }),

  // PATCH /api/doctor/appointments/{id}/status  { status: 'COMPLETED' | 'NO_SHOW' }
  // Guarded server-side: only the appointment's own doctor may call it.
  // A 403 here means "not your appointment" (the caller shows its own message) —
  // it must not be read as an expired session and log the doctor out.
  updateStatus: (id, status) =>
    api.patch(`/doctor/appointments/${id}/status`, { status }, { treat403AsForbidden: true }),
};

// ── Triage reports (mostly read-only from triage_results) ────────────────────
export const triageReportAPI = {
  // GET /api/doctor/triage-reports?patientId=
  listByPatient: (patientId) =>
    api.get('/doctor/triage-reports', { params: { patientId }, ...LOAD }),

  // PUT /api/doctor/triage-reports/{id}/highlighted-markers
  // Body: { doctorEmail, markers: [{ canonicalCode, markerName }, ...] } — at
  // most 2. Names the report's Health Journey milestone label on the
  // patient's Today screen — see backend HealthJourneyService and
  // WorstMarkerResolver (the data-driven fallback used until a doctor picks).
  setHighlightedMarkers: (triageResultId, data) =>
    api.put(`/doctor/triage-reports/${triageResultId}/highlighted-markers`, data),
};

// ── Doctor's own action items (doctor_action_items table) ────────────────────
export const doctorActionItemAPI = {
  // GET /api/doctor/action-items?patientId=
  listByPatient: (patientId) =>
    api.get('/doctor/action-items', { params: { patientId }, ...LOAD }),

  // GET /api/doctor/action-items/open — open items across patients, minus the
  // ones the triage/escalation pipeline generated (the Notes tab). A doctor
  // gets their own caseload; navigators/admins get every patient.
  listOpen: () => api.get('/doctor/action-items/open', LOAD),

  // POST /api/doctor/action-items  { patientId, doctorId, description, priority, dueDate }
  create: (data) => api.post('/doctor/action-items', data),

  // PUT /api/doctor/action-items/{id}  { completed: true|false }
  update: (id, data) => api.put(`/doctor/action-items/${id}`, data, { treat403AsForbidden: true }),
};

// ── Clinical notes ────────────────────────────────────────────────────────────
export const doctorNoteAPI = {
  // GET /api/doctor/notes?patientId=
  listByPatient: (patientId) =>
    api.get('/doctor/notes', { params: { patientId }, ...LOAD }),

  // POST /api/doctor/notes  { patientId, doctorId, content }
  create: (data) => api.post('/doctor/notes', data),
};

// ── Lifestyle recommendation items (AI-generated, doctor review gate) ────────
//
// A patient never sees one of these until a doctor accepts it — see
// LifestyleRecommendationService.java and DoctorPortalService.acceptLifestyleItem().
// listByPatient returns every status (PENDING_REVIEW/ACCEPTED/REJECTED) across
// the patient's whole history, same scope as doctorActionItemAPI/doctorNoteAPI.
export const doctorLifestyleItemAPI = {
  // GET /api/doctor/lifestyle-items?patientId=
  listByPatient: (patientId) =>
    api.get('/doctor/lifestyle-items', { params: { patientId }, ...LOAD }),

  // POST /api/doctor/lifestyle-items/{id}/accept  { doctorEmail, priority }
  // Creates a doctor_action_items row (source=AI_ACCEPTED) and flows into the
  // patient's existing care plan — see CarePlanAssembler.
  accept: (id, doctorEmail, priority) =>
    api.post(`/doctor/lifestyle-items/${id}/accept`, { doctorEmail, priority }),

  // POST /api/doctor/lifestyle-items/{id}/reject  { doctorEmail }
  reject: (id, doctorEmail) =>
    api.post(`/doctor/lifestyle-items/${id}/reject`, { doctorEmail }),

  // POST /api/doctor/lifestyle-items/bulk  { ids, action: 'ACCEPT'|'REJECT', priority? }
  // At most 50 ids. Always 200 with { succeeded: [id], failed: [{ id, reason }] },
  // reason one of NOT_FOUND, NOT_YOUR_PATIENT, ALREADY_REVIEWED, FAILED.
  // priority (ACCEPT only) is left unset, as the single accept sends it.
  bulk: (ids, action, priority) =>
    api.post('/doctor/lifestyle-items/bulk', { ids, action, priority }),
};

// ── CRITICAL escalation SLA (Sprint 11) ──────────────────────────────────────
//
// Powers EscalationSlaBadge in DoctorTriageView — the 2h SLA countdown timer.
// Constitution §III: "SLA: navigator must acknowledge CRITICAL within 2 hours."
//
// Note: TriageReportDto from the backend (DoctorPortalService Sprint 11)
// already includes escalationStatus + escalationNotifiedAt. This API is a
// belt-and-braces fallback for the deployment transition window and for
// standalone escalation management views.
export const doctorEscalationAPI = {
  // GET /api/doctor/critical-escalations?patientId=
  // Returns all CriticalEscalation rows for a patient, newest first.
  listByPatient: (patientId) =>
    api.get('/doctor/critical-escalations', { params: { patientId }, ...LOAD }),

  // POST /api/doctor/critical-escalations/{id}/acknowledge
  // Body: { acknowledgedBy: navigatorId, note: string }
  acknowledge: (escalationId, data) =>
    api.post(`/doctor/critical-escalations/${escalationId}/acknowledge`, data),

  // ── Queue + workflow ────────────────────────────────────────────────────
  // GET /api/doctor/critical-escalations?view=&assignedToMe=
  // view: OPEN (default) | IN_PROGRESS | SNOOZED | ACTIVE | RESOLVED
  // Doctors only ever receive their own patients' rows.
  list: (params) => api.get('/doctor/critical-escalations', { params }),

  // Body: { outcome, note } — outcome is one of RESOLUTION_OUTCOMES in
  // components/EscalationQueue.jsx; a note is required for FALSE_POSITIVE.
  resolve: (id, data) => api.post(`/doctor/critical-escalations/${id}/resolve`, data),

  // DOCTOR/ADMIN. Body: { navigatorId?, note? } — no navigatorId means the
  // patient's own navigator. The doctor decides, the navigator does the outreach.
  assignToNavigator: (id, data) =>
    api.post(`/doctor/critical-escalations/${id}/assign-navigator`, data || {}),

  // Body: { hours: 1-72, reason } — the reason is required, and a snooze
  // always expires: the escalation comes back as a fresh alert.
  snooze: (id, data) => api.post(`/doctor/critical-escalations/${id}/snooze`, data),

  // DOCTOR/ADMIN. Body: { specialty, note? }. Suggest only — the navigator books.
  suggestSpecialist: (id, data) =>
    api.post(`/doctor/critical-escalations/${id}/suggest-specialist`, data),

  // NAVIGATOR/ADMIN. Body: { note? } — confirms the suggested specialist is booked.
  markSpecialistBooked: (id, data) =>
    api.post(`/doctor/critical-escalations/${id}/specialist-booked`, data || {}),

  // Body: { ids, action: ACKNOWLEDGE | RESOLVE | ASSIGN_NAVIGATOR, outcome?, note? }
  // → { succeeded: [id], failed: [{ id, reason }] } — partial success is normal.
  bulk: (data) => api.post('/doctor/critical-escalations/bulk', data),
};
